import { CancelToken } from 'axios'
import moment from 'moment'
import { path } from 'ramda'

import {
  FetchPlaybookPageParams,
  FetchPlaybooksParams,
  Playbook,
  PlaybookExperimentVersion,
  PlaybookMetricsPageResponse,
  PlaybookStatus,
} from '@driftt/api-types'

import { ConversationAPI } from 'api'
import { CHAT_REPORT_TYPES, FAST_LANE_REPORT_TYPES } from 'constants/playbooks'
import { cancelCatcher, mapResponse } from 'utils/api'

import {
  parseFetchPlaybookMetricsResponse,
  parseFetchPlaybooksResponse,
} from './parsers'
import {
  IFetchPlaybooksMetricsParams,
  IPlaybooksMetricsResponse,
  PlaybookNamesById,
  PlaybooksById,
} from './types'

export const fetchPlaybooksById = (
  playbookIds: number[],
  cancelToken?: CancelToken
) =>
  ConversationAPI.post<{ playbooks: Playbook[] }>(
    '/playbooks/by_id',
    playbookIds,
    {
      cancelToken,
      params: { includeActions: true },
    }
  )
    .then(mapResponse)
    .then(({ playbooks }) => parseFetchPlaybooksResponse(playbooks))
    .catch(cancelCatcher)

export const fetchPlaybooks = (
  params?: FetchPlaybooksParams
): Promise<PlaybooksById> =>
  ConversationAPI.get('/playbooks', { params })
    .then(mapResponse)
    .then(parseFetchPlaybooksResponse)

export const fetchAllChatPlaybooks = () =>
  fetchPlaybooks({
    status: [PlaybookStatus.DISABLED, PlaybookStatus.ENABLED],
    reportType: CHAT_REPORT_TYPES,
  })

export const fetchAllFastlanePlaybooks = () =>
  fetchPlaybooks({
    status: [PlaybookStatus.ENABLED],
    reportType: FAST_LANE_REPORT_TYPES,
  })

export const fetchAllChatPlaybooksIncludingArchive = () =>
  fetchPlaybooks({
    status: [
      PlaybookStatus.DISABLED,
      PlaybookStatus.ENABLED,
      PlaybookStatus.ARCHIVED,
    ],
    reportType: CHAT_REPORT_TYPES,
  })

export const fetchPlaybooksPage = (
  params: FetchPlaybookPageParams,
  cancelToken?: CancelToken
): Promise<PlaybookMetricsPageResponse> =>
  ConversationAPI.get('/playbooks/page', { params, cancelToken })
    .then(mapResponse)
    .catch(cancelCatcher)

export const fetchPlaybooksMetrics = (
  params: IFetchPlaybooksMetricsParams
): Promise<IPlaybooksMetricsResponse[]> =>
  ConversationAPI.get('/playbooks/metrics', {
    params: {
      ...params,
      startDate: moment(params.startDate).format('YYYY-MM-DD'),
      endDate: moment(params.endDate).format('YYYY-MM-DD'),
    },
  }).then(mapResponse)

export const fetchPlaybookMetricsParallel = (
  params: IFetchPlaybooksMetricsParams
): Promise<{ [key: string]: number }> => {
  const { startDate, endDate } = params
  const playbookIds: number[] = Array.isArray(params.playbookIds)
    ? params.playbookIds
    : [params.playbookIds]
  const promises = () =>
    playbookIds.map((playbookId) =>
      fetchPlaybooksMetrics({
        startDate,
        endDate,
        playbookIds: playbookId,
      })
    )

  return Promise.all(promises())
    .then((response: IPlaybooksMetricsResponse[][]) =>
      response.map((data) => path([0], data) || {})
    )
    .then(parseFetchPlaybookMetricsResponse)
}

export const fetchPlaybookNamesByIds = (
  playbookIds: number[],
  cancelToken?: CancelToken
): Promise<PlaybookNamesById> =>
  ConversationAPI.post('/playbooks/names_for_ids', playbookIds, {
    cancelToken,
  })
    .then(mapResponse)
    .catch(cancelCatcher)

export const fetchPlaybookExperimentVersions = (playbookId: number) =>
  ConversationAPI.get<PlaybookExperimentVersion[]>(
    `/playbooks/${playbookId}/versions/published/ab_tests`
  )
